import { Typography } from '@components'
import { productChannel, useEventBusOn } from '@haus-storefront-react/core'
import { useTranslation } from 'react-i18next'
import type { StockStatusAttributes } from './_propsFn'
import styles from './StockStatus.module.scss'

type StockStatusKey = 'inStock' | 'lowStock' | 'outOfStock'

const stockStatusMap: Record<string, StockStatusKey> = {
  IN_STOCK: 'inStock',
  LOW_STOCK: 'lowStock',
  OUT_OF_STOCK: 'outOfStock',
}

const dotClassByStatus: Record<StockStatusKey, string> = {
  inStock: styles.dotInStock,
  lowStock: styles.dotLowStock,
  outOfStock: styles.dotOutOfStock,
}

const StockStatusWidget = ({ configStyles }: StockStatusAttributes) => {
  const { t } = useTranslation()
  const [selectedProductVariant] = useEventBusOn(
    productChannel,
    'product:variant:selected',
    undefined,
    undefined,
    false,
  )

  if (!selectedProductVariant) {
    return null
  }

  const statusKey: StockStatusKey =
    stockStatusMap[selectedProductVariant.stockLevel] ?? 'outOfStock'
  const statusLabel = t(`stockStatus.${statusKey}`)

  return (
    <div
      className={styles.root}
      role="status"
      aria-live="polite"
      aria-label={t('a11y.stockStatus.aria.ariaLabel', { stockStatus: statusLabel })}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="6"
        height="6"
        viewBox="0 0 6 6"
        fill="none"
        className={dotClassByStatus[statusKey]}
        aria-hidden
      >
        <circle cx="3" cy="3" r="3" />
      </svg>
      <Typography
        variant={configStyles?.stockStatusTypography?.variant ?? 'body'}
        as={configStyles?.stockStatusTypography?.tag ?? 'span'}
        color={configStyles?.stockStatusTypography?.color ?? 'body'}
        className={styles.text}
      >
        {statusLabel}
      </Typography>
    </div>
  )
}

export default StockStatusWidget
